import api from '../axios.config';
import { API_ENDPOINTS } from '../endpoints';
import {
  CreditAccountDTO,
  ServiceRateDTO,
  CreditTransactionDTO,
  InvoiceDTO,
  PaymentDTO,
  UsageSummaryDTO,
  BillingStatsDTO,
  CreateCreditAccountRequest,
  CreateServiceRateRequest,
  AddCreditsRequest,
  DeductCreditsRequest,
  CreateInvoiceRequest,
  RecordPaymentRequest,
  InvoiceStatus,
  TransactionType,
  BillingProfileDTO,
  BillingProfileReference,
  AlertKind,
  AlertSettingsDTO,
  AlertSettingsRequest,
  BalanceAlertDTO,
  BusinessUsageDTO,
  DailyStatementDTO,
  DeferredRevenueDTO,
  EnforcementDTO,
  EnforcementRequest,
  ConvertPreviewDTO,
  ShortfallDTO,
  InvoicePreviewDTO,
  SpringPage,
  SuspensionEventDTO,
  UsageDashboardDTO,
  CreditGrantDTO,
  ExpiringGrantsDTO,
  GrantStatus,
  TestAdjustmentPreviewDTO,
  TestAdjustmentResultDTO,
  TestCallReportDTO,
  TestNumberDTO,
  TestNumberRequest,
} from '../../types/billing';
import type { MyReceivables, MySummary } from '../../pages/Billing/myBillingRules';
import type { RefusalSummary } from '../../pages/Billing/refusalCountRules';

// API Response types
interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
}

/**
 * A billing-profile save. On a 400 the server names each bad field in `fieldErrors`
 * (field -> reason); `status` lets the form tell "not allowed" (403) from "invalid".
 */
export interface BillingProfileSaveResult {
  success: boolean;
  data?: BillingProfileDTO;
  error?: string;
  fieldErrors?: Record<string, string>;
  status?: number;
}

interface PaginatedData<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
}

/**
 * Billing Service
 * Connects to the backend Billing API for CPaaS platform
 */

/** A blank filter means "any": send no parameter rather than an empty one the server must parse. */
const withoutBlank = <T extends object>(params?: T): Partial<T> =>
  Object.fromEntries(Object.entries(params || {}).filter(([, v]) => v !== '' && v !== null && v !== undefined)) as Partial<T>;

/**
 * Saves an .xlsx response as a file. Fetched as a blob because the endpoints need the
 * Authorization header, so a plain link cannot download them; the server names the file.
 */
const saveXlsx = (res: { data: any; headers?: any }, fallbackName: string) => {
  const disposition = String(res.headers?.['content-disposition'] || '');
  const named = /filename="?([^"]+)"?/.exec(disposition)?.[1];
  const url = window.URL.createObjectURL(new Blob([res.data], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  }));
  const a = document.createElement('a');
  a.href = url;
  a.download = named || fallbackName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => window.URL.revokeObjectURL(url), 0);
};

/** The server's message, else a 403/404/400 wording, else the fallback. */
const failure = (error: any, fallback: string, forbidden?: string): string => {
  const status = error?.response?.status;
  const message = error?.response?.data?.message;
  if (message && typeof message === 'string') return message;
  if (status === 403) return forbidden || 'You do not have access to this';
  if (status === 404) return 'Not found';
  return fallback;
};

class BillingService {
  private baseUrl = '/billing';

  // ==================== Credit Accounts ====================

  async getCreditAccounts(params?: {
    page?: number;
    size?: number;
  }): Promise<ApiResponse<PaginatedData<CreditAccountDTO>>> {
    try {
      const response = await api.get(`${this.baseUrl}/accounts`, {
        params: { ...params, page: params?.page !== undefined ? Math.max(0, params.page - 1) : 0 },
      });
      return {
        success: true,
        data: response.data,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch credit accounts',
      };
    }
  }

  async getCreditAccount(id: number): Promise<ApiResponse<CreditAccountDTO>> {
    try {
      const response = await api.get(`${this.baseUrl}/accounts/${id}`);
      return {
        success: true,
        data: response.data,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch credit account',
      };
    }
  }

  async getCreditAccountByOrganization(organizationId: string): Promise<ApiResponse<CreditAccountDTO>> {
    try {
      const response = await api.get(`${this.baseUrl}/accounts/organization/${organizationId}`);
      return {
        success: true,
        data: response.data,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch credit account',
      };
    }
  }

  async createCreditAccount(request: CreateCreditAccountRequest): Promise<ApiResponse<CreditAccountDTO>> {
    try {
      const response = await api.post(`${this.baseUrl}/accounts`, request);
      return {
        success: true,
        data: response.data,
        message: 'Credit account created successfully',
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to create credit account',
      };
    }
  }

  async updateCreditAccount(id: number, request: CreateCreditAccountRequest): Promise<ApiResponse<CreditAccountDTO>> {
    try {
      const response = await api.put(`${this.baseUrl}/accounts/${id}`, request);
      return {
        success: true,
        data: response.data,
        message: 'Credit account updated successfully',
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to update credit account',
      };
    }
  }

  async checkBalance(organizationId: string): Promise<ApiResponse<number>> {
    try {
      const response = await api.get(`${this.baseUrl}/accounts/organization/${organizationId}/balance`);
      return {
        success: true,
        data: response.data,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to check balance',
      };
    }
  }

  /**
   * Postpaid → prepaid as of `asOf` (a server UTC LocalDateTime). The server refuses
   * with 409, and says why, while usage before `asOf` is uninvoiced or a usage invoice
   * is unpaid. Platform admins only.
   */
  async convertToPrepaid(
    organizationId: string,
    asOf: string,
    settledOutsideReference?: string,
  ): Promise<ApiResponse<CreditAccountDTO>> {
    try {
      const response = await api.post(
        `${this.baseUrl}/accounts/organization/${encodeURIComponent(organizationId)}/convert-to-prepaid`,
        null,
        { params: withoutBlank({ asOf, settledOutsideReference: settledOutsideReference?.trim() }) },
      );
      return { success: true, data: response.data, message: 'Account is now prepaid' };
    } catch (error: any) {
      return { success: false, error: failure(error, 'Failed to convert to prepaid') };
    }
  }

  /** What converting at `asOf` (server UTC LocalDateTime) would find; read-only. */
  async getConvertToPrepaidPreview(organizationId: string, asOf: string): Promise<ApiResponse<ConvertPreviewDTO>> {
    try {
      const response = await api.get(
        `${this.baseUrl}/accounts/organization/${encodeURIComponent(organizationId)}/convert-to-prepaid/preview`,
        { params: { asOf } },
      );
      return { success: true, data: response.data };
    } catch (error: any) {
      return { success: false, error: failure(error, 'Failed to preview the conversion') };
    }
  }

  /** A prepaid account below zero: what it owes, and any shortfall invoice already open. */
  async getShortfall(organizationId: string): Promise<ApiResponse<ShortfallDTO>> {
    try {
      const response = await api.get(
        `${this.baseUrl}/accounts/organization/${encodeURIComponent(organizationId)}/shortfall`,
      );
      return { success: true, data: response.data };
    } catch (error: any) {
      return { success: false, error: failure(error, 'Failed to load the shortfall') };
    }
  }

  /** A DRAFT credit-purchase invoice for the shortfall. 409 (with the reason) when nothing is owed or one is open. */
  async createShortfallInvoice(organizationId: string): Promise<ApiResponse<InvoiceDTO>> {
    try {
      const response = await api.post(
        `${this.baseUrl}/accounts/organization/${encodeURIComponent(organizationId)}/shortfall-invoice`,
      );
      return { success: true, data: response.data };
    } catch (error: any) {
      return { success: false, error: failure(error, 'Failed to create the shortfall invoice') };
    }
  }

  // ==================== Credit Operations ====================

  async addCredits(request: AddCreditsRequest): Promise<ApiResponse<CreditTransactionDTO>> {
    try {
      const response = await api.post(`${this.baseUrl}/credits/add`, request);
      return {
        success: true,
        data: response.data,
        message: 'Credits added successfully',
      };
    } catch (error: any) {
      return {
        success: false,
        error: failure(error, error?.response?.status === 400 ? 'The credit terms were refused' : 'Failed to add credits'),
      };
    }
  }

  // ==================== Credit grants & refunds ====================

  /** One organisation's grants, newest first. `page` is 1-based. */
  async getGrants(params: {
    organizationId: string;
    status?: GrantStatus | '';
    page?: number;
    size?: number;
  }): Promise<ApiResponse<SpringPage<CreditGrantDTO>>> {
    try {
      const response = await api.get(API_ENDPOINTS.BILLING_GRANTS, {
        params: { ...withoutBlank({ ...params, page: undefined }), page: Math.max(0, (params.page ?? 1) - 1) },
      });
      return { success: true, data: response.data };
    } catch (error: any) {
      return { success: false, error: failure(error, 'Failed to load credit grants') };
    }
  }

  /** `days`: 1–365. */
  async getExpiringGrants(organizationId: string, days = 30): Promise<ApiResponse<ExpiringGrantsDTO>> {
    try {
      const response = await api.get(API_ENDPOINTS.BILLING_GRANTS_EXPIRING, { params: { organizationId, days } });
      return { success: true, data: response.data };
    } catch (error: any) {
      return { success: false, error: failure(error, 'Failed to load expiring credit') };
    }
  }

  /** 409 when the grant is partly used or otherwise not voidable; the server says which. */
  async voidGrant(id: number, reason?: string): Promise<ApiResponse<CreditGrantDTO>> {
    try {
      const response = await api.post(API_ENDPOINTS.BILLING_GRANT_VOID(id), withoutBlank({ reason }));
      return { success: true, data: response.data };
    } catch (error: any) {
      return {
        success: false,
        error: failure(error, error?.response?.status === 409 ? 'This grant cannot be voided' : 'Failed to void the grant'),
      };
    }
  }

  /**
   * Refunds one usage charge as a CREDIT_REFUND ledger row. Idempotent: refunding the
   * same charge again answers with the first refund. 409 for postpaid, invoiced or
   * non-usage rows, with the server's reason.
   */
  async refundTransaction(transactionId: number, reason?: string): Promise<ApiResponse<CreditTransactionDTO>> {
    try {
      const response = await api.post(API_ENDPOINTS.BILLING_CREDIT_REFUND, { transactionId, ...withoutBlank({ reason }) });
      return { success: true, data: response.data };
    } catch (error: any) {
      return {
        success: false,
        error: failure(error, error?.response?.status === 409 ? 'This charge cannot be refunded' : 'Failed to refund'),
      };
    }
  }

  async deductCredits(request: DeductCreditsRequest): Promise<ApiResponse<CreditTransactionDTO>> {
    try {
      const response = await api.post(`${this.baseUrl}/credits/deduct`, request);
      return {
        success: true,
        data: response.data,
        message: 'Credits deducted successfully',
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to deduct credits',
      };
    }
  }

  async adjustCredits(
    organizationId: string,
    amount: number,
    description: string
  ): Promise<ApiResponse<CreditTransactionDTO>> {
    try {
      const response = await api.post(`${this.baseUrl}/credits/adjust`, null, {
        params: { organizationId, amount, description },
      });
      return {
        success: true,
        data: response.data,
        message: 'Credits adjusted successfully',
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to adjust credits',
      };
    }
  }

  // ==================== Service Rates ====================

  async getServiceRates(params?: {
    organizationId?: string;
    page?: number;
    size?: number;
  }): Promise<ApiResponse<PaginatedData<ServiceRateDTO>>> {
    try {
      const response = await api.get(`${this.baseUrl}/rates`, {
        params: { ...params, page: params?.page !== undefined ? Math.max(0, params.page - 1) : 0 },
      });
      return {
        success: true,
        data: response.data,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch service rates',
      };
    }
  }

  async getDefaultRates(): Promise<ApiResponse<ServiceRateDTO[]>> {
    try {
      const response = await api.get(`${this.baseUrl}/rates/default`);
      return {
        success: true,
        data: response.data,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch default rates',
      };
    }
  }

  async getServiceRate(id: number): Promise<ApiResponse<ServiceRateDTO>> {
    try {
      const response = await api.get(`${this.baseUrl}/rates/${id}`);
      return {
        success: true,
        data: response.data,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch service rate',
      };
    }
  }

  async createServiceRate(request: CreateServiceRateRequest): Promise<ApiResponse<ServiceRateDTO>> {
    try {
      const response = await api.post(`${this.baseUrl}/rates`, request);
      return {
        success: true,
        data: response.data,
        message: 'Service rate created successfully',
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to create service rate',
      };
    }
  }

  async updateServiceRate(id: number, request: CreateServiceRateRequest): Promise<ApiResponse<ServiceRateDTO>> {
    try {
      const response = await api.put(`${this.baseUrl}/rates/${id}`, request);
      return {
        success: true,
        data: response.data,
        message: 'Service rate updated successfully',
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to update service rate',
      };
    }
  }

  async deleteServiceRate(id: number): Promise<ApiResponse<void>> {
    try {
      await api.delete(`${this.baseUrl}/rates/${id}`);
      return {
        success: true,
        message: 'Service rate deleted successfully',
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to delete service rate',
      };
    }
  }

  // ==================== Invoices ====================

  /** `from`/`to`: IST invoice dates, `YYYY-MM-DD`, both inclusive (invoice dates are IST days). */
  async getInvoices(params?: {
    organizationId?: string;
    status?: InvoiceStatus;
    from?: string;
    to?: string;
    page?: number;
    size?: number;
  }): Promise<ApiResponse<PaginatedData<InvoiceDTO>>> {
    try {
      const response = await api.get(`${this.baseUrl}/invoices`, {
        params: { ...withoutBlank(params), page: params?.page !== undefined ? Math.max(0, params.page - 1) : 0 },
      });
      return {
        success: true,
        data: response.data,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch invoices',
      };
    }
  }

  async getInvoice(id: number): Promise<ApiResponse<InvoiceDTO>> {
    try {
      const response = await api.get(`${this.baseUrl}/invoices/${id}`);
      return {
        success: true,
        data: response.data,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch invoice',
      };
    }
  }

  async createInvoice(request: CreateInvoiceRequest): Promise<ApiResponse<InvoiceDTO>> {
    try {
      const response = await api.post(`${this.baseUrl}/invoices`, request);
      return {
        success: true,
        data: response.data,
        message: 'Invoice created successfully',
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to create invoice',
      };
    }
  }

  async updateInvoiceStatus(id: number, status: InvoiceStatus): Promise<ApiResponse<InvoiceDTO>> {
    try {
      const response = await api.patch(`${this.baseUrl}/invoices/${id}/status`, null, {
        params: { status },
      });
      return {
        success: true,
        data: response.data,
        message: 'Invoice status updated successfully',
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to update invoice status',
      };
    }
  }

  async finalizeInvoice(id: number): Promise<ApiResponse<InvoiceDTO>> {
    try {
      const response = await api.post(`${this.baseUrl}/invoices/${id}/finalize`);
      return {
        success: true,
        data: response.data,
        message: 'Invoice finalized successfully',
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to finalize invoice',
      };
    }
  }

  /**
   * Invoices every usage charge before `before` (a server UTC LocalDateTime) that is
   * not on an invoice yet, as a DRAFT with 18% GST. The server answers 204 when there
   * was nothing to invoice, which is not a failure. Platform admins only.
   */
  async invoiceUsage(
    organizationId: string,
    before: string,
    options?: { taxPercent?: number; dueInDays?: number },
  ): Promise<ApiResponse<InvoiceDTO | null>> {
    try {
      const response = await api.post(`${this.baseUrl}/invoices/from-usage`, null, {
        params: { organizationId, before, ...options },
      });
      if (response.status === 204 || !response.data) {
        return { success: true, data: null, message: 'No uninvoiced usage before that cut-off' };
      }
      return { success: true, data: response.data, message: `Draft ${response.data.invoiceNumber} created` };
    } catch (error: any) {
      return { success: false, error: error.response?.data?.message || 'Failed to invoice usage' };
    }
  }

  // ==================== Payments ====================

  /** `from`/`to`: UTC LocalDateTimes, `[from, to)` on the payment time; see istDaysToServerUtc. */
  async getPayments(params?: {
    organizationId?: string;
    from?: string;
    to?: string;
    page?: number;
    size?: number;
  }): Promise<ApiResponse<PaginatedData<PaymentDTO>>> {
    try {
      const response = await api.get(`${this.baseUrl}/payments`, {
        params: { ...withoutBlank(params), page: params?.page !== undefined ? Math.max(0, params.page - 1) : 0 },
      });
      return {
        success: true,
        data: response.data,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch payments',
      };
    }
  }

  async getPayment(id: number): Promise<ApiResponse<PaymentDTO>> {
    try {
      const response = await api.get(`${this.baseUrl}/payments/${id}`);
      return {
        success: true,
        data: response.data,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch payment',
      };
    }
  }

  async recordPayment(request: RecordPaymentRequest): Promise<ApiResponse<PaymentDTO>> {
    try {
      const response = await api.post(`${this.baseUrl}/payments`, request);
      return {
        success: true,
        data: response.data,
        message: 'Payment recorded successfully',
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to record payment',
      };
    }
  }

  // ==================== Monthly voice statement ====================

  /**
   * The month's voice bill from the ledger (.xlsx, the invoice workbook's layout:
   * summary per payer, business_billing per business). Fetched as a blob because the
   * endpoint needs the Authorization header; the server names the file.
   * Platform admins only — anyone else gets a 403.
   */
  async downloadVoiceStatement(month: string): Promise<{ ok: true } | { ok: false; error: string }> {
    try {
      const res = await api.get(`${this.baseUrl}/statements/voice`, { params: { month }, responseType: 'blob' });
      saveXlsx(res, `voice_statement_${month}.xlsx`);
      return { ok: true };
    } catch (error: any) {
      const status = error.response?.status;
      return {
        ok: false,
        error: status === 403 ? 'Only platform admins can download the voice statement'
          : status === 400 ? 'Pick a month' : 'Could not download the voice statement',
      };
    }
  }

  // ==================== Transactions ====================

  /** `from`/`to`: UTC LocalDateTimes, `[from, to)` on created_at; see istDaysToServerUtc. */
  async getTransactions(params: {
    organizationId: string;
    type?: TransactionType;
    from?: string;
    to?: string;
    page?: number;
    size?: number;
  }): Promise<ApiResponse<PaginatedData<CreditTransactionDTO>>> {
    try {
      const response = await api.get(`${this.baseUrl}/transactions`, {
        params: { ...withoutBlank(params), page: params?.page !== undefined ? Math.max(0, params.page - 1) : 0 },
      });
      return {
        success: true,
        data: response.data,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch transactions',
      };
    }
  }

  // ==================== Usage ====================

  async getUsageSummaries(params: {
    organizationId: string;
    page?: number;
    size?: number;
  }): Promise<ApiResponse<PaginatedData<UsageSummaryDTO>>> {
    try {
      const response = await api.get(`${this.baseUrl}/usage`, {
        params: { ...params, page: params?.page !== undefined ? Math.max(0, params.page - 1) : 0 },
      });
      return {
        success: true,
        data: response.data,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch usage summaries',
      };
    }
  }

  async getUsageForPeriod(
    organizationId: string,
    startDate: string,
    endDate: string
  ): Promise<ApiResponse<UsageSummaryDTO[]>> {
    try {
      const response = await api.get(`${this.baseUrl}/usage/period`, {
        params: { organizationId, startDate, endDate },
      });
      return {
        success: true,
        data: response.data,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch usage for period',
      };
    }
  }

  // ==================== Billing profiles ====================

  /** States, countries, currencies and the enums the profile form picks from. */
  async getBillingProfileReference(): Promise<ApiResponse<BillingProfileReference>> {
    try {
      const response = await api.get(API_ENDPOINTS.BILLING_PROFILE_REFERENCE);
      return { success: true, data: response.data };
    } catch (error: any) {
      return { success: false, error: error.response?.data?.message || 'Failed to load billing reference data' };
    }
  }

  /** The saved profile, or a prefilled draft with `configured: false` when none is saved yet. */
  async getBillingProfile(organizationId: string): Promise<ApiResponse<BillingProfileDTO>> {
    try {
      const response = await api.get(API_ENDPOINTS.BILLING_PROFILE(organizationId));
      return { success: true, data: response.data };
    } catch (error: any) {
      const status = error.response?.status;
      return {
        success: false,
        error: error.response?.data?.message
          || (status === 403 ? 'You cannot view this organisation\'s billing profile'
            : status === 404 ? 'Organisation not found' : 'Failed to load the billing profile'),
      };
    }
  }

  /**
   * Create or replace. Only the organisation above this one (or the platform) may
   * save; anyone else gets a 403. A 400 carries `{ message, errors: { field: reason } }`.
   */
  async saveBillingProfile(organizationId: string, profile: BillingProfileDTO): Promise<BillingProfileSaveResult> {
    try {
      const response = await api.put(API_ENDPOINTS.BILLING_PROFILE(organizationId), profile);
      return { success: true, data: response.data, status: response.status };
    } catch (error: any) {
      const status: number | undefined = error.response?.status;
      const body = error.response?.data || {};
      const fieldErrors = body.errors && typeof body.errors === 'object' ? (body.errors as Record<string, string>) : undefined;
      return {
        success: false,
        status,
        fieldErrors,
        error: body.message
          || (status === 403 ? 'Only the organisation that bills this one, or a platform admin, can change its billing profile'
            : status === 404 ? 'Organisation not found' : 'Failed to save the billing profile'),
      };
    }
  }

  // ==================== Credit control (per account) ====================

  /** Balance alerts and daily statements sent for the account, newest first. `page` is 1-based. */
  async getBalanceAlerts(
    organizationId: string,
    params?: { kind?: AlertKind | ''; page?: number; size?: number },
  ): Promise<ApiResponse<SpringPage<BalanceAlertDTO>>> {
    try {
      const response = await api.get(API_ENDPOINTS.BILLING_ALERTS(organizationId), {
        params: { ...withoutBlank({ kind: params?.kind, size: params?.size }), page: Math.max(0, (params?.page ?? 1) - 1) },
      });
      return { success: true, data: response.data };
    } catch (error: any) {
      return { success: false, error: failure(error, 'Failed to load alerts') };
    }
  }

  async getAlertSettings(organizationId: string): Promise<ApiResponse<AlertSettingsDTO>> {
    try {
      const response = await api.get(API_ENDPOINTS.BILLING_ALERT_SETTINGS(organizationId));
      return { success: true, data: response.data };
    } catch (error: any) {
      return { success: false, error: failure(error, 'Failed to load alert settings') };
    }
  }

  /** 400 (bad tiers) carries the server's reason in `error`. */
  async updateAlertSettings(organizationId: string, body: AlertSettingsRequest): Promise<ApiResponse<AlertSettingsDTO>> {
    try {
      const response = await api.put(API_ENDPOINTS.BILLING_ALERT_SETTINGS(organizationId), body);
      return { success: true, data: response.data };
    } catch (error: any) {
      return {
        success: false,
        error: failure(error, 'Failed to save alert settings',
          'Only the organisation that bills this account, or a platform admin, can change its alerts'),
      };
    }
  }

  /** `date`: the IST day, `YYYY-MM-DD`; omitted = yesterday (IST). */
  async getDailyStatement(organizationId: string, date?: string): Promise<ApiResponse<DailyStatementDTO>> {
    try {
      const response = await api.get(API_ENDPOINTS.BILLING_DAILY_STATEMENT(organizationId), {
        params: withoutBlank({ date }),
      });
      return { success: true, data: response.data };
    } catch (error: any) {
      return { success: false, error: failure(error, 'Failed to load the daily statement') };
    }
  }

  async getEnforcement(organizationId: string): Promise<ApiResponse<EnforcementDTO>> {
    try {
      const response = await api.get(API_ENDPOINTS.BILLING_ENFORCEMENT(organizationId));
      return { success: true, data: response.data };
    } catch (error: any) {
      return { success: false, error: failure(error, 'Failed to load enforcement') };
    }
  }

  async updateEnforcement(organizationId: string, body: EnforcementRequest): Promise<ApiResponse<EnforcementDTO>> {
    try {
      const response = await api.put(API_ENDPOINTS.BILLING_ENFORCEMENT(organizationId), body);
      return { success: true, data: response.data };
    } catch (error: any) {
      return {
        success: false,
        error: failure(error, 'Failed to save enforcement',
          'Only the organisation that bills this account, or a platform admin, can change its enforcement'),
      };
    }
  }

  /** Suspension audit, newest first. `page` is 1-based. */
  async getSuspensionEvents(
    organizationId: string,
    params?: { page?: number; size?: number },
  ): Promise<ApiResponse<SpringPage<SuspensionEventDTO>>> {
    try {
      const response = await api.get(API_ENDPOINTS.BILLING_SUSPENSION_EVENTS(organizationId), {
        params: { ...withoutBlank({ size: params?.size }), page: Math.max(0, (params?.page ?? 1) - 1) },
      });
      return { success: true, data: response.data };
    } catch (error: any) {
      return { success: false, error: failure(error, 'Failed to load suspension events') };
    }
  }

  // ==================== Reports ====================

  /**
   * "The invoice so far": what invoicing usage would produce, nothing written.
   * `before` is a server UTC LocalDateTime (default now). Platform staff must name the org.
   */
  async getInvoicePreview(params: {
    organizationId: string;
    before?: string;
    taxPercent?: number;
  }): Promise<ApiResponse<InvoicePreviewDTO>> {
    try {
      const response = await api.get(API_ENDPOINTS.BILLING_INVOICE_PREVIEW, { params: withoutBlank(params) });
      return { success: true, data: response.data };
    } catch (error: any) {
      return { success: false, error: failure(error, 'Failed to load the invoice so far') };
    }
  }

  /** `from`/`to`: IST days, both inclusive, at most 400 days apart. */
  async getUsageDashboard(params: {
    organizationId?: string;
    from: string;
    to: string;
    businessId?: string;
  }): Promise<ApiResponse<UsageDashboardDTO>> {
    try {
      const response = await api.get(API_ENDPOINTS.BILLING_USAGE_DASHBOARD, { params: withoutBlank(params) });
      return { success: true, data: response.data };
    } catch (error: any) {
      return { success: false, error: failure(error, 'Failed to load usage') };
    }
  }

  async getTopBusinesses(params: {
    organizationId?: string;
    from: string;
    to: string;
    limit?: number;
  }): Promise<ApiResponse<BusinessUsageDTO[]>> {
    try {
      const response = await api.get(API_ENDPOINTS.BILLING_USAGE_TOP_BUSINESSES, { params: withoutBlank(params) });
      return { success: true, data: response.data };
    } catch (error: any) {
      return { success: false, error: failure(error, 'Failed to load top businesses') };
    }
  }

  /** `month`: IST `YYYY-MM`. Without an organisation: every prepaid account (platform staff only). */
  async getDeferredRevenue(params: { month: string; organizationId?: string }): Promise<ApiResponse<DeferredRevenueDTO>> {
    try {
      const response = await api.get(API_ENDPOINTS.BILLING_DEFERRED_REVENUE, { params: withoutBlank(params) });
      return { success: true, data: response.data };
    } catch (error: any) {
      return {
        success: false,
        error: failure(error, 'Failed to load deferred revenue',
          'Only platform staff can see every account; pick an organisation'),
      };
    }
  }

  /** The same report as .xlsx, saved the way the voice statement is. */
  async downloadDeferredRevenue(params: { month: string; organizationId?: string }): Promise<{ ok: true } | { ok: false; error: string }> {
    try {
      const res = await api.get(API_ENDPOINTS.BILLING_DEFERRED_REVENUE_XLSX, {
        params: withoutBlank(params),
        responseType: 'blob',
      });
      saveXlsx(res, `deferred_revenue_${params.month}${params.organizationId ? `_${params.organizationId}` : ''}.xlsx`);
      return { ok: true };
    } catch (error: any) {
      const status = error.response?.status;
      return {
        ok: false,
        error: status === 403 ? 'Only platform staff can download every account; pick an organisation'
          : status === 400 ? 'Pick a month' : 'Could not download the deferred revenue report',
      };
    }
  }

  // ==================== My billing (self-service) ====================

  async getMySummary(params: { month: string; organizationId?: string }): Promise<ApiResponse<MySummary>> {
    try {
      const response = await api.get(API_ENDPOINTS.BILLING_MY_SUMMARY, { params: withoutBlank(params) });
      return { success: true, data: response.data };
    } catch (error: any) {
      return { success: false, error: failure(error, 'Failed to load your billing') };
    }
  }

  async getMyRefusals(params: { days: number; organizationId?: string }): Promise<ApiResponse<RefusalSummary>> {
    try {
      const response = await api.get(API_ENDPOINTS.BILLING_MY_REFUSALS, { params: withoutBlank(params) });
      return { success: true, data: response.data };
    } catch (error: any) {
      return { success: false, error: failure(error, 'Failed to load refused calls') };
    }
  }

  async getMyBusinesses(params: { month: string; organizationId?: string }): Promise<ApiResponse<MyReceivables>> {
    try {
      const response = await api.get(API_ENDPOINTS.BILLING_MY_BUSINESSES, { params: withoutBlank(params) });
      return { success: true, data: response.data };
    } catch (error: any) {
      return { success: false, error: failure(error, 'Failed to load your businesses') };
    }
  }

  async downloadMyStatement(params: { month: string; organizationId?: string }): Promise<{ ok: true } | { ok: false; error: string }> {
    try {
      const res = await api.get(API_ENDPOINTS.BILLING_MY_STATEMENT_XLSX, {
        params: withoutBlank(params),
        responseType: 'blob',
      });
      saveXlsx(res, `billing_statement_${params.month}.xlsx`);
      return { ok: true };
    } catch (error: any) {
      const status = error.response?.status;
      return {
        ok: false,
        error: status === 400 ? 'Pick a month that has started' : status === 404 ? 'Not found'
          : 'Could not download the statement',
      };
    }
  }

  // ==================== Test call numbers (platform staff only) ====================

  async getTestNumbers(): Promise<ApiResponse<TestNumberDTO[]>> {
    try {
      const response = await api.get(API_ENDPOINTS.BILLING_TEST_NUMBERS);
      return { success: true, data: response.data };
    } catch (error: any) {
      return { success: false, error: failure(error, 'Failed to load test numbers', 'Platform staff only') };
    }
  }

  async createTestNumber(body: TestNumberRequest): Promise<ApiResponse<TestNumberDTO>> {
    try {
      const response = await api.post(API_ENDPOINTS.BILLING_TEST_NUMBERS, body);
      return { success: true, data: response.data };
    } catch (error: any) {
      return { success: false, error: failure(error, 'Could not add the test number', 'Platform staff only') };
    }
  }

  async updateTestNumber(id: number, body: TestNumberRequest): Promise<ApiResponse<TestNumberDTO>> {
    try {
      const response = await api.put(API_ENDPOINTS.BILLING_TEST_NUMBER(id), body);
      return { success: true, data: response.data };
    } catch (error: any) {
      return { success: false, error: failure(error, 'Could not save the test number', 'Platform staff only') };
    }
  }

  /** Ends the number's use at `until` (server UTC; omitted = now). There is no delete. */
  async deactivateTestNumber(id: number, until?: string): Promise<ApiResponse<TestNumberDTO>> {
    try {
      const response = await api.post(API_ENDPOINTS.BILLING_TEST_NUMBER_DEACTIVATE(id), null, {
        params: withoutBlank({ until }),
      });
      return { success: true, data: response.data };
    } catch (error: any) {
      return { success: false, error: failure(error, 'Could not end the test number', 'Platform staff only') };
    }
  }

  /** Business-wise test calls over IST days. */
  async getTestCallReport(params: { from: string; to: string; organizationId?: string }): Promise<ApiResponse<TestCallReportDTO>> {
    try {
      const response = await api.get(API_ENDPOINTS.BILLING_TEST_CALL_REPORT, { params: withoutBlank(params) });
      return { success: true, data: response.data };
    } catch (error: any) {
      return { success: false, error: failure(error, 'Failed to load the test-call report', 'Platform staff only') };
    }
  }

  /** What "adjust past charges" would reverse; writes nothing. `from` is server UTC. */
  async previewTestAdjustment(from?: string): Promise<ApiResponse<TestAdjustmentPreviewDTO>> {
    try {
      const response = await api.get(API_ENDPOINTS.BILLING_TEST_ADJUSTMENT_PREVIEW, { params: withoutBlank({ from }) });
      return { success: true, data: response.data };
    } catch (error: any) {
      return { success: false, error: failure(error, 'Failed to preview the adjustment', 'Platform staff only') };
    }
  }

  /** Applies exactly the previewed adjustment; a 409 means the ledger changed — preview again. */
  async applyTestAdjustment(body: { from?: string; previewHash: string }): Promise<ApiResponse<TestAdjustmentResultDTO>> {
    try {
      const response = await api.post(API_ENDPOINTS.BILLING_TEST_ADJUSTMENT_APPLY, withoutBlank(body));
      return { success: true, data: response.data };
    } catch (error: any) {
      return { success: false, error: failure(error, 'Could not apply the adjustment', 'Platform staff only') };
    }
  }

  // ==================== Statistics ====================

  /** Platform-wide figures for an IST month (`YYYY-MM`); omitted = the current month. */
  async getStats(month?: string): Promise<ApiResponse<BillingStatsDTO>> {
    try {
      const response = await api.get(`${this.baseUrl}/stats`, { params: month ? { month } : undefined });
      return {
        success: true,
        data: response.data,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch billing statistics',
      };
    }
  }
}

export const billingService = new BillingService();
